/**
 * 拣选策略域 Mock 数据和 Mock 函数
 * 对应后端 PickingStrategyConfigController 的 7 个接口
 */

import type { ApiResponse, PageData } from '../../types/common';
import type {
  PickingStrategyConfigDTO,
  WeightedItemLimitConfigDTO,
  TaskSkuLimitConfigDTO,
  TaskCompletionTimeLimitDTO,

  SaveWeightedItemLimitRequest,
  SaveTaskSkuLimitRequest,
  SaveTaskCompletionTimeLimitRequest,
  SaveSplitByCustomAreaRequest,
  SavePickingTaskSortPolicyRequest,
} from '../../types/picking-strategy/pickingStrategy';
import { ConfigStatus } from '../../constants/pickingStrategy';

// Mock 工具函数 - 成功响应
function mockSuccess<T>(data: T): ApiResponse<T> {
  return {
    success: true,
    errorMessage: null,
    flag: 'success',
    data,
    traceId: `mock-${Date.now()}`,
  };
}

// Mock 聚合数据存储（按 storeCode 键控）
const mockStrategyStore: Record<string, PickingStrategyConfigDTO> = {
  STORE001: {
    storeCode: 'STORE001',
    storeName: 'Lotus Silom',
    company: 'lotus',
    splitByCustomArea: true,
    sortRules: [
      { sortItem: 'ORDER_CREATE_TIME', sortType: 'DESC', priority: 1 },
    ],
    weightedItemLimit: {
      id: 1,
      code: 'WIL001',
      configCode: 'WIL001',
      upperLimitPercent: 10,
      lowerLimitPercent: 10,
      storeCode: 'STORE001',
      storeName: 'Lotus Silom',
      status: ConfigStatus.ENABLED,
      creatorAccount: 'admin',
      creatorName: 'System Admin',
      gmtCreate: Date.now() - 86400000 * 7,
      modifierAccount: 'admin',
      modifierName: 'System Admin',
      gmtModified: Date.now() - 86400000,
    },
    taskCompletionTimeLimit: {
      id: 1,
      code: 'TCTL001',
      configCode: 'TCTL001',
      maxCompletionMinutes: 30,
      storeCode: 'STORE001',
      storeName: 'Lotus Silom',
      status: ConfigStatus.ENABLED,
      creatorAccount: 'admin',
      creatorName: 'System Admin',
      gmtCreate: Date.now() - 86400000 * 6,
      modifierAccount: 'admin',
      modifierName: 'System Admin',
      gmtModified: Date.now() - 86400000 * 2,
    },
    taskSkuLimit: {
      id: 1,
      code: 'TSL001',
      configCode: 'TSL001',
      maxSkuPerTask: 50,
      storeCode: 'STORE001',
      storeName: 'Lotus Silom',
      status: ConfigStatus.ENABLED,
      creatorAccount: 'admin',
      creatorName: 'System Admin',
      gmtCreate: Date.now() - 86400000 * 12,
      modifierAccount: 'admin',
      modifierName: 'System Admin',
      gmtModified: Date.now() - 86400000 * 5,
    },
    updatedTime: Date.now() - 86400000,
    updatedBy: 'System Admin',
  },
  STORE002: {
    storeCode: 'STORE002',
    storeName: 'Lotus Rama IV',
    company: 'lotus',
    splitByCustomArea: false,
    sortRules: [],
    weightedItemLimit: {
      id: 2,
      code: 'WIL002',
      configCode: 'WIL002',
      upperLimitPercent: 15,
      lowerLimitPercent: 5,
      storeCode: 'STORE002',
      storeName: 'Lotus Rama IV',
      status: ConfigStatus.ENABLED,
      creatorAccount: 'admin',
      creatorName: 'System Admin',
      gmtCreate: Date.now() - 86400000 * 5,
      modifierAccount: 'admin',
      modifierName: 'System Admin',
      gmtModified: Date.now() - 86400000 * 2,
    },
    taskCompletionTimeLimit: null,
    taskSkuLimit: {
      id: 2,
      code: 'TSL002',
      configCode: 'TSL002',
      maxSkuPerTask: 30,
      storeCode: 'STORE002',
      storeName: 'Lotus Rama IV',
      status: ConfigStatus.ENABLED,
      creatorAccount: 'admin',
      creatorName: 'System Admin',
      gmtCreate: Date.now() - 86400000 * 4,
      modifierAccount: 'admin',
      modifierName: 'System Admin',
      gmtModified: Date.now() - 86400000 * 1,
    },
    updatedTime: Date.now() - 86400000 * 2,
    updatedBy: 'admin',
  },
  default: {
    storeCode: 'STORE001',
    storeName: 'Lotus Silom',
    company: 'lotus',
    splitByCustomArea: true,
    sortRules: [{ sortItem: 'ORDER_CREATE_TIME', sortType: 'DESC', priority: 1 }],
    weightedItemLimit: {
      id: 1,
      code: 'WIL001',
      configCode: 'WIL001',
      upperLimitPercent: 10,
      lowerLimitPercent: 10,
      storeCode: 'STORE001',
      storeName: 'Lotus Silom',
      status: ConfigStatus.ENABLED,
      creatorAccount: 'admin',
      creatorName: 'System Admin',
      gmtCreate: Date.now() - 86400000 * 7,
      modifierAccount: 'admin',
      modifierName: 'System Admin',
      gmtModified: Date.now() - 86400000,
    },
    taskCompletionTimeLimit: {
      id: 1,
      code: 'TCTL001',
      configCode: 'TCTL001',
      maxCompletionMinutes: 30,
      storeCode: 'STORE001',
      storeName: 'Lotus Silom',
      status: ConfigStatus.ENABLED,
      creatorAccount: 'admin',
      creatorName: 'System Admin',
      gmtCreate: Date.now() - 86400000 * 6,
      modifierAccount: 'admin',
      modifierName: 'System Admin',
      gmtModified: Date.now() - 86400000 * 2,
    },
    taskSkuLimit: {
      id: 1,
      code: 'TSL001',
      configCode: 'TSL001',
      maxSkuPerTask: 50,
      storeCode: 'STORE001',
      storeName: 'Lotus Silom',
      status: ConfigStatus.ENABLED,
      creatorAccount: 'admin',
      creatorName: 'System Admin',
      gmtCreate: Date.now() - 86400000 * 12,
      modifierAccount: 'admin',
      modifierName: 'System Admin',
      gmtModified: Date.now() - 86400000 * 5,
    },
    updatedTime: Date.now() - 86400000,
    updatedBy: 'System Admin',
  },
};


// 获取门店拣选策略配置 Mock
export function mockGetPickingStrategyConfig(storeCode: string): Promise<ApiResponse<PickingStrategyConfigDTO>> {
  const data = mockStrategyStore[storeCode] ?? {
    storeCode,
    storeName: `Store ${storeCode}`,
    company: 'lotus',
    weightedItemLimit: null,
    taskCompletionTimeLimit: null,
    taskSkuLimit: null,
    splitByCustomArea: null,
    sortRules: [],
    updatedTime: 0,
    updatedBy: '',
  };
  return Promise.resolve(mockSuccess(data));
}

// 保存称重商品上下限配置 Mock
export function mockSaveWeightedItemLimit(data: SaveWeightedItemLimitRequest): Promise<ApiResponse<WeightedItemLimitConfigDTO>> {
  const existing = mockStrategyStore[data.storeCode];
  const now = Date.now();
  const result: WeightedItemLimitConfigDTO = {
    id: existing?.weightedItemLimit?.id ?? now,
    code: existing?.weightedItemLimit?.code ?? `WIL${now}`,
    configCode: existing?.weightedItemLimit?.configCode ?? `WIL${now}`,
    upperLimitPercent: data.upperLimitPercent,
    lowerLimitPercent: data.lowerLimitPercent,
    storeCode: data.storeCode,
    storeName: existing?.storeName ?? data.storeCode,
    status: ConfigStatus.ENABLED,
    creatorAccount: 'admin',
    creatorName: 'System Admin',
    gmtCreate: existing?.weightedItemLimit?.gmtCreate ?? now,
    modifierAccount: 'admin',
    modifierName: 'System Admin',
    gmtModified: now,
  };
  if (mockStrategyStore[data.storeCode]) {
    mockStrategyStore[data.storeCode].weightedItemLimit = result;
    mockStrategyStore[data.storeCode].updatedTime = now;
  }
  return Promise.resolve(mockSuccess(result));
}

// 保存任务 SKU 上限配置 Mock
export function mockSaveTaskSkuLimit(data: SaveTaskSkuLimitRequest): Promise<ApiResponse<TaskSkuLimitConfigDTO>> {
  const existing = mockStrategyStore[data.storeCode];
  const now = Date.now();
  const result: TaskSkuLimitConfigDTO = {
    id: existing?.taskSkuLimit?.id ?? now,
    code: existing?.taskSkuLimit?.code ?? `TSL${now}`,
    configCode: existing?.taskSkuLimit?.configCode ?? `TSL${now}`,
    maxSkuPerTask: data.maxSkuPerTask,
    storeCode: data.storeCode,
    storeName: existing?.storeName ?? data.storeCode,
    status: ConfigStatus.ENABLED,
    creatorAccount: 'admin',
    creatorName: 'System Admin',
    gmtCreate: existing?.taskSkuLimit?.gmtCreate ?? now,
    modifierAccount: 'admin',
    modifierName: 'System Admin',
    gmtModified: now,
  };
  if (mockStrategyStore[data.storeCode]) {
    mockStrategyStore[data.storeCode].taskSkuLimit = result;
    mockStrategyStore[data.storeCode].updatedTime = now;
  }
  return Promise.resolve(mockSuccess(result));
}

// 保存拣选任务完成时间上限配置 Mock
export function mockSaveTaskCompletionTimeLimit(data: SaveTaskCompletionTimeLimitRequest): Promise<ApiResponse<TaskCompletionTimeLimitDTO>> {
  const existing = mockStrategyStore[data.storeCode];
  const now = Date.now();
  const result: TaskCompletionTimeLimitDTO = {
    id: existing?.taskCompletionTimeLimit?.id ?? now,
    code: existing?.taskCompletionTimeLimit?.code ?? `TCTL${now}`,
    configCode: existing?.taskCompletionTimeLimit?.configCode ?? `TCTL${now}`,
    maxCompletionMinutes: data.maxCompletionMinutes,
    storeCode: data.storeCode,
    storeName: existing?.storeName ?? data.storeCode,
    status: ConfigStatus.ENABLED,
    creatorAccount: 'admin',
    creatorName: 'System Admin',
    gmtCreate: existing?.taskCompletionTimeLimit?.gmtCreate ?? now,
    modifierAccount: 'admin',
    modifierName: 'System Admin',
    gmtModified: now,
  };
  if (mockStrategyStore[data.storeCode]) {
    mockStrategyStore[data.storeCode].taskCompletionTimeLimit = result;
    mockStrategyStore[data.storeCode].updatedTime = now;
  }
  return Promise.resolve(mockSuccess(result));
}

// 保存按自定义区域拆分拣选任务配置 Mock
export function mockSaveSplitByCustomArea(data: SaveSplitByCustomAreaRequest): Promise<ApiResponse<null>> {
  if (mockStrategyStore[data.storeCode]) {
    mockStrategyStore[data.storeCode].splitByCustomArea = data.splitByCustomArea;
    mockStrategyStore[data.storeCode].updatedTime = Date.now();
  }
  return Promise.resolve(mockSuccess(null));
}

// 保存 App 端拣选任务排序策略 Mock
export function mockSavePickingTaskSortPolicy(data: SavePickingTaskSortPolicyRequest): Promise<ApiResponse<null>> {
  if (mockStrategyStore[data.storeCode]) {
    mockStrategyStore[data.storeCode].sortRules = data.sortRules;
    mockStrategyStore[data.storeCode].updatedTime = Date.now();
  }
  return Promise.resolve(mockSuccess(null));
}
